import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Menu } from 'lucide-react';
import { Amplify } from 'aws-amplify';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import Sidebar from './components/Sidebar';
import TripsPage from './pages/TripsPage';
import CreateTripPage from './pages/CreateTripPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import FlightTestPage from './pages/FlightTestPage';
import FeedbackPage from './pages/FeedbackPage';
import ProfilePromptModal from './components/ProfilePromptModal';
import changeDetectionService from './services/changeDetectionService';
import { getResponsiveMainContent, colors } from './styles/designTokens';
import awsconfig from './aws-exports';
import { FEATURES } from './config/features';
import BetaLoginFlow from './components/BetaLoginFlow';
import InviteAcceptPage from './pages/InviteAcceptPage';

// Configure Amplify
Amplify.configure(awsconfig);

const fetchTrips = async (authToken, userId) => {
  // Use the trips service that includes localStorage merging
  const { getTrips } = await import('./services/trips');
  
  const trips = await getTrips(userId);
  
  return trips;
};

function AppContent() {
  const { user, signOut } = useAuthenticator();
  const queryClient = useQueryClient();
  const [authToken, setAuthToken] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [showArchived, setShowArchived] = useState(false); // null = show all, true = show past, false = show upcoming
  const [currentPage, setCurrentPage] = useState('trips');
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [hasShownProfilePrompt, setHasShownProfilePrompt] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get the JWT token for API calls
  const getAuthToken = useCallback(async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error fetching auth token:', error);
      return null;
    }
  }, []);

  // Fetch user attributes
  const fetchUserAttrs = useCallback(async () => {
    try {
      if (user) {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
      }
    } catch (error) {
      console.error('Error fetching user attributes:', error);
      setUserAttributes(null);
    }
  }, [user]);

  // Set auth token on component mount
  useEffect(() => {
    const fetchToken = async () => {
      const token = await getAuthToken();
      setAuthToken(token);
    };
    fetchToken();
  }, [user, getAuthToken]);

  // Fetch user attributes when user changes
  useEffect(() => {
    if (user) {
      fetchUserAttrs();
      
      // Check if user has ever seen the profile prompt
      const userId = user?.sub || user?.userId || user?.username;
      if (userId) {
        const profilePromptKey = `travelflow_profile_prompt_shown_${userId}`;
        const hasShownPrompt = localStorage.getItem(profilePromptKey) === 'true';
        setHasShownProfilePrompt(hasShownPrompt);
        
               // Check for changes since last visit
               const checkInitialChanges = async () => {
                 try {
                   await changeDetectionService.checkForChanges(userId);
                   // Silent change detection
                 } catch (error) {
                   // Silent error handling
                 }
               };
        
        checkInitialChanges();
      }
    }
  }, [user, fetchUserAttrs]);

  const { data: trips = [], isLoading, error, refetch } = useQuery(
    'trips',
    async () => {
      const token = await getAuthToken();
      const userId = user?.sub || user?.userId || user?.username;
      return fetchTrips(token, userId);
    },
    {
      enabled: !!user,
      retry: 1
    }
  );

  // Refetch trips when navigating to trips page
  useEffect(() => {
    if (currentPage === 'trips') {
      refetch();
    }
  }, [currentPage, refetch]);

  const handleTripCreated = (newTrip) => {
    queryClient.invalidateQueries('trips');
    toast.success(`Trip "${newTrip.tripName}" created successfully!`);
    setCurrentPage('trips'); // Navigate to trips page after creation
    
    // Check if this is the user's first trip and they haven't seen the profile prompt
    const userId = user?.sub || user?.userId || user?.username;
    if (userId && !hasShownProfilePrompt) {
      // Check if this was their first trip by looking at current trips count
      const currentTrips = queryClient.getQueryData('trips') || [];
      if (currentTrips.length === 0) {
        // This is their first trip, show profile prompt after a short delay
        setTimeout(() => {
          setShowProfilePrompt(true);
        }, 1500); // 1.5 second delay to let the success message show
      }
    }
  };

  const handleTripDeleted = (_tripId) => {
    queryClient.invalidateQueries('trips');
    toast.success('Trip deleted successfully!');
  };

  const handleTripUpdated = (updatedTrip) => {
    queryClient.setQueryData('trips', (oldTrips) =>
      (oldTrips || []).map((t) =>
        t.tripId === updatedTrip.tripId ? updatedTrip : t
      )
    );
    queryClient.invalidateQueries('trips');
  };

  const handleProfilePromptClose = () => {
    setShowProfilePrompt(false);
    
    // Mark that user has seen the profile prompt
    const userId = user?.sub || user?.userId || user?.username;
    if (userId) {
      const profilePromptKey = `travelflow_profile_prompt_shown_${userId}`;
      localStorage.setItem(profilePromptKey, 'true');
      setHasShownProfilePrompt(true);
    }
  };

  const handleGoToProfile = () => {
    setShowProfilePrompt(false);
    setCurrentPage('profile');
    
    // Mark that user has seen the profile prompt
    const userId = user?.sub || user?.userId || user?.username;
    if (userId) {
      const profilePromptKey = `travelflow_profile_prompt_shown_${userId}`;
      localStorage.setItem(profilePromptKey, 'true');
      setHasShownProfilePrompt(true);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully!');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out');
    }
  };

  // Filter and sort trips based on date and archive status
  const filteredTrips = trips
    .filter(trip => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const tripEndDate = new Date(trip.endDate);
      tripEndDate.setHours(23, 59, 59, 999); // Set to end of day
      
      const isPastTrip = tripEndDate < today;
      
      // Handle two states: true (show past), false (show upcoming)
      if (showArchived === true) {
        return isPastTrip; // Show only past trips
      } else {
        return !isPastTrip; // Show only upcoming trips
      }
    })
    .sort((a, b) => {
      // Sort by start date
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      
      if (showArchived === true) {
        // For past trips, sort by start date descending (most recent first)
        return dateB - dateA;
      } else {
        // For upcoming trips, sort by start date ascending (soonest first)
        return dateA - dateB;
      }
    });

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'trips':
        return (
          <TripsPage
            trips={trips}
            filteredTrips={filteredTrips}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            isLoading={isLoading}
            error={error}
            refetch={refetch}
            userAttributes={userAttributes}
            onTripDeleted={handleTripDeleted}
            onTripUpdated={handleTripUpdated}
            onTripCreated={handleTripCreated}
            authToken={authToken}
            user={user}
          />
        );
      case 'create':
        return (
          <CreateTripPage
            onTripCreated={handleTripCreated}
            authToken={authToken}
            user={user}
          />
        );
      case 'profile':
        return (
          <ProfilePage
            user={user}
            userAttributes={userAttributes}
          />
        );
      case 'notifications':
        return (
          <NotificationsPage
            user={user}
            userAttributes={userAttributes}
          />
        );
      case 'flightTest':
        return (
          <FlightTestPage
            user={user}
            userAttributes={userAttributes}
            onTripCreated={handleTripCreated}
            onNavigate={setCurrentPage}
          />
        );
      case 'feedback':
        return <FeedbackPage />;
      default:
        return (
          <TripsPage
            trips={trips}
            filteredTrips={filteredTrips}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            isLoading={isLoading}
            error={error}
            refetch={refetch}
            userAttributes={userAttributes}
            onTripDeleted={handleTripDeleted}
            onTripUpdated={handleTripUpdated}
            onTripCreated={handleTripCreated}
            authToken={authToken}
            user={user}
          />
        );
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.pageBackground,
      display: 'flex'
    }}>
      {/* Hamburger Menu Button (Mobile Only) — hidden while the sidebar is
          open since it sits at the same fixed position as the sidebar's own
          header and would otherwise overlap the "TravelFlow" wordmark; the
          sidebar has its own close (X) button. */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            position: 'fixed',
            top: '16px',
            left: '16px',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            background: 'white',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            cursor: 'pointer',
            color: '#374151',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'}
          onMouseLeave={(e) => e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'}
        >
          <Menu size={24} />
        </button>
      )}

      {/* Sidebar */}
      <Sidebar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSignOut={handleSignOut}
        userAttributes={userAttributes}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isMobile={isMobile}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
      />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        ...getResponsiveMainContent(isMobile),
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
        {/* Page Content */}
        <div style={{
          flex: 1,
          overflow: 'hidden'
        }}>
          {renderCurrentPage()}
        </div>
      </div>

      {/* Profile Prompt Modal */}
      <ProfilePromptModal
        isVisible={showProfilePrompt}
        onClose={handleProfilePromptClose}
        onGoToProfile={handleGoToProfile}
      />

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    </div>
  );
}

/**
 * Check if this is an invite acceptance URL
 */
function App() {
  // Check if this is an invite acceptance URL
  if (window.location.pathname === '/invite') {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      return <InviteAcceptPage token={token} />;
    }
  }

  const betaMode = FEATURES.BETA_MODE;

  if (betaMode === 'private') {
    return <PrivateBetaApp />;
  }

  // Public mode: use standard Authenticator
  return (
    <Authenticator
      hideSignUp={false}
      signUpAttributes={['email', 'given_name', 'family_name']}
    >
      <AuthenticatedApp />
    </Authenticator>
  );
}

/**
 * PrivateBetaApp — checks for an existing Amplify session.
 * If the user is already authenticated, renders AuthenticatedApp.
 * If not authenticated, renders BetaLoginFlow for the magic-link
 * invite onboarding.
 */
function PrivateBetaApp() {
  const [authState, setAuthState] = useState('checking'); // 'checking' | 'authenticated' | 'unauthenticated'

  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        if (!cancelled) {
          if (session?.tokens?.idToken) {
            setAuthState('authenticated');
          } else {
            setAuthState('unauthenticated');
          }
        }
      } catch {
        if (!cancelled) {
          setAuthState('unauthenticated');
        }
      }
    };

    checkSession();
    return () => { cancelled = true; };
  }, []);

  if (authState === 'checking') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 100dvh (not 100vh) so this centers correctly against the real
        // visible mobile Safari viewport on load — see BetaLandingPage.js.
        minHeight: '100dvh',
        background: colors.primary,
        color: '#ffffff',
        fontSize: '18px'
      }}>
        Checking access...
      </div>
    );
  }

  if (authState === 'authenticated') {
    return (
      <Authenticator>
        <AuthenticatedApp />
      </Authenticator>
    );
  }

  return <BetaLoginFlow />;
}

/**
 * Runs inside <Authenticator>. The user is authenticated, so render
 * the full app directly. New users arrive via the magic-link invite
 * flow (InviteAcceptPage), which creates the Cognito account — there
 * is no separate invite-code gate.
 */
function AuthenticatedApp() {
  return <AppContent />;
}

export default App;